import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { meApi } from "@/api/me";
import { projectsApi } from "@/api/projects";
import { plansApi, type PromoValidateResult } from "@/api/plans";
import { Dialog } from "@/components/ui/Dialog";
import { ApiError } from "@/api/client";
import { Plus, Clock } from "lucide-react";

export const Route = createFileRoute("/app/")({
  component: AppHome,
});

function AppHome() {
  const { data, isLoading } = useQuery({ queryKey: ["me"], queryFn: meApi.get });
  const [createOpen, setCreateOpen] = useState(false);

  // Server enforces one owned project per user. Mirror the rule on the
  // FE so the create button is hidden when the user is already an owner —
  // members of someone else's project (developer/billing) still get the
  // button so they can spin up their own.
  const ownsProject = !!data?.memberships?.some((m) => m.role === "owner");

  // Pay-then-project: a pending intent has no projects/projectMembers
  // row yet, so it's invisible to `ownsProject`. Without checking this
  // explicitly, the user can land on the dashboard in a second tab and
  // try to create another project — the server returns 409, but the
  // UI lets them get all the way through the form before that. Treat
  // a pending intent as "you already have a project in flight."
  const pendingIntent = data?.pending_intent ?? null;
  const canCreate = !ownsProject && !pendingIntent;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Welcome back</h1>
          <p className="mt-1 text-ink/65">{isLoading ? "Loading…" : data?.user?.email ?? ""}</p>
        </div>
        {canCreate && (
          <button onClick={() => setCreateOpen(true)} className="btn-primary">
            <Plus size={16} className="mr-1" /> New project
          </button>
        )}
      </div>

      {pendingIntent && (
        <Link
          to="/signup/pay/$paymentId"
          params={{ paymentId: pendingIntent.payment_id }}
          className="card flex items-start gap-4 border-peach-500/25 bg-peach-500/10 transition hover:border-peach-500/40"
        >
          <span className="grid h-10 w-10 flex-none place-items-center rounded-full bg-peach text-ink-500">
            <Clock size={18} />
          </span>
          <div className="flex-1">
            <div className="text-xs font-medium uppercase tracking-wider text-peach-500">
              Payment pending
            </div>
            <div className="mt-0.5 text-base font-medium text-ink">
              Finish setting up <span className="font-semibold">{pendingIntent.intent_name}</span>
            </div>
            <p className="mt-1 text-sm text-ink/65">
              Your project will be created once we see the on-chain payment. Pick up where you left off →
            </p>
          </div>
        </Link>
      )}

      <section>
        <h2 className="text-lg font-medium">Your projects</h2>
        {isLoading ? (
          <p className="mt-2 text-sm text-ink/55">Loading…</p>
        ) : data && data.memberships && data.memberships.length > 0 ? (
          <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.memberships.map((m) => (
              <li key={m.project_id}>
                <Link to="/app/projects/$pid" params={{ pid: m.project_id }} className="card block transition hover:border-ink/15">
                  <div className="text-sm uppercase tracking-wide text-ink/55">{m.role}</div>
                  <div className="mt-1 text-base font-medium">{m.name}</div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="card mt-4">
            <p className="text-sm text-ink/55">
              {pendingIntent ? "Your first project is in flight — finish payment above to activate it." : "No projects yet."}
            </p>
            {canCreate && (
              <button onClick={() => setCreateOpen(true)} className="btn-primary mt-4">Create your first project</button>
            )}
          </div>
        )}
      </section>

      <CreateProjectDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function CreateProjectDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (b: boolean) => void }) {
  const [name, setName] = useState("");
  const [planId, setPlanId] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState<PromoValidateResult | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Re-validate (and clear) the applied promo whenever the plan changes —
  // the discounted price is plan-specific and a code may not apply to the
  // newly-selected plan.
  useEffect(() => {
    setPromo(null);
  }, [planId]);

  async function applyPromo() {
    const code = promoCode.trim();
    if (!code || !planId) return;
    setPromoChecking(true);
    try {
      const res = await plansApi.validatePromo(code, planId, "buy");
      setPromo(res);
    } catch {
      setPromo({ ok: false, message: "Couldn't validate that code." });
    } finally {
      setPromoChecking(false);
    }
  }

  const { data: plansResp } = useQuery({
    queryKey: ["plans"],
    queryFn: plansApi.list,
    enabled: open, // only fetch when the dialog is opened
  });
  const plans = plansResp ?? [];

  // Pre-select first available plan once the list loads. Subsequent
  // opens keep whatever the user chose unless they cleared it.
  useEffect(() => {
    if (open && !planId && plans.length > 0) setPlanId(plans[0]!.id);
  }, [open, planId, plans]);

  const m = useMutation({
    mutationFn: (input: { name: string; plan_id: string; promo_code?: string }) =>
      projectsApi.create(input.name, input.plan_id, input.promo_code),
    // Pay-then-project: the server returned a payment_id + deposit
    // address, not a project. Route to /signup/pay/<id>; once the
    // payment confirms, that page navigates to the new project.
    // Exception: a 100%-off comp activates the project inline, so route
    // straight to it.
    onSuccess: async (intent) => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      onOpenChange(false);
      setName("");
      if (intent.is_comp && intent.project_id) {
        navigate({ to: "/app/projects/$pid", params: { pid: intent.project_id } });
        return;
      }
      navigate({
        to: "/signup/pay/$paymentId",
        params: { paymentId: intent.payment_id },
      });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed to create project"),
  });

  // Only send a promo code that validated successfully against this plan.
  const appliedPromoCode =
    promo?.ok && promo.code ? promoCode.trim() : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Create project" description="You'll be the owner. You can rename it later.">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          if (!name.trim() || !planId) return;
          m.mutate({ name: name.trim(), plan_id: planId, promo_code: appliedPromoCode });
        }}
        className="space-y-4"
      >
        <div>
          <label className="label">Project name</label>
          <input className="input mt-1" autoFocus required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="label">Plan</label>
          {plans.length === 0 ? (
            <p className="mt-1 text-sm text-ink/55">Loading plans…</p>
          ) : (
            <ul className="mt-2 grid gap-2 sm:grid-cols-2">
              {plans.map((p) => {
                const selected = p.id === planId;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setPlanId(p.id)}
                      className={`w-full rounded-md border p-3 text-left transition ${
                        selected
                          ? "border-primary-500 ring-1 ring-primary-500 bg-primary-50/50 "
                          : "border-ink/8 hover:border-ink/15 :border-ink/30"
                      }`}
                    >
                      <div className="flex items-baseline justify-between">
                        <span className="font-medium">{p.display_name}</span>
                        <span className="text-sm">${p.monthly_price_usd}/mo</span>
                      </div>
                      <div className="mt-1 text-xs text-ink/55 ">
                        {Number(p.monthly_credits).toLocaleString()} credits · {p.rps_limit} req/s
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div>
          <label className="label">Promo code <span className="text-ink/45">(optional)</span></label>
          <div className="mt-1 flex gap-2">
            <input
              className="input flex-1"
              placeholder="e.g. EARLY30"
              value={promoCode}
              onChange={(e) => {
                setPromoCode(e.target.value);
                setPromo(null);
              }}
            />
            <button
              type="button"
              className="btn-secondary"
              disabled={!promoCode.trim() || !planId || promoChecking}
              onClick={applyPromo}
            >
              {promoChecking ? "Checking…" : "Apply"}
            </button>
          </div>
          {promo && !promo.ok && (
            <p className="mt-1 text-sm text-red-600">{promo.message ?? "Invalid promo code."}</p>
          )}
          {promo?.ok && (
            <p className="mt-1 text-sm text-green-700">
              <span className="font-medium">{promo.code}</span> applied —{" "}
              {promo.is_comp ? (
                <span>free ({promo.scope === "lifetime" ? "recurring" : "one-time"})</span>
              ) : (
                <>
                  <span className="line-through text-ink/45">${promo.list_price_usd}</span>{" "}
                  <span className="font-medium">${promo.net_usd}</span>
                  {promo.scope === "lifetime" ? " every cycle" : " first cycle"}
                </>
              )}
            </p>
          )}
        </div>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => onOpenChange(false)} className="btn-secondary">Cancel</button>
          <button type="submit" className="btn-primary" disabled={m.isPending || !planId}>
            {m.isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
